/**
 * Step 2 · Commodity & Geology
 * Primary commodity + deposit type + mineralisation style.
 */
import { useQuery } from "@tanstack/react-query";
import { Package, MapTrifold, Sparkle } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import SearchableSelect from "../parts/SearchableSelect";
import { FieldRow, StepHeader } from "./Step1Identity";

export default function Step2Commodity({ form, set }) {
  const commoditiesQ = useQuery({
    queryKey: ["master-commodities"],
    queryFn:  () => api.get("/master/commodities").then((r) => r.data.results),
    staleTime: 5 * 60_000,
  });
  const depositsQ = useQuery({
    queryKey: ["master-deposit-types"],
    queryFn:  () => api.get("/master/deposit-types").then((r) => r.data.results),
    staleTime: 5 * 60_000,
  });

  const commodity  = (commoditiesQ.data || []).find((c) => c.id === form.commodity);
  const density = commodity?.density_range;

  return (
    <section className="space-y-4" data-testid="step-2-commodity">
      <StepHeader step="Step 2" title="Commodity & Geology"
        subtitle="Choose the primary commodity and deposit type. This drives density defaults, cut-off grade suggestions and estimation heuristics." />

      <div className="rounded-lg bg-white border border-slate-200 divide-y divide-slate-100">
        <FieldRow icon={Package} label="Primary Commodity" required
                     hint="Groups: Precious · Base · Ferrous · Battery · Industrial · Energy · Gemstones.">
          <SearchableSelect
            testId="select-commodity"
            value={form.commodity}
            options={(commoditiesQ.data || []).map((c) => ({
              value: c.id, label: c.name,
              hint: (c.symbols || [])[0] || "", meta: c.group,
            }))}
            onChange={(v) => set({ commodity: v })}
            placeholder="Search commodity…"
            groupBy="meta"
          />
        </FieldRow>

        <FieldRow icon={MapTrifold} label="Deposit Type"
                     hint="Refines block size, search radius, and mineralisation defaults.">
          <SearchableSelect
            testId="select-deposit-type"
            value={form.deposit_type}
            options={(depositsQ.data || []).map((d) => ({
              value: d.id, label: d.name, meta: d.group,
            }))}
            onChange={(v) => set({ deposit_type: v })}
            placeholder="Search deposit type…"
            groupBy="meta"
          />
        </FieldRow>

        <FieldRow icon={Sparkle} label="Mineralisation Style"
                     hint="Optional narrative — e.g. 'shear-hosted vein', 'stockwork', 'BIF'.">
          <input type="text" value={form.mineralisation_style || ""}
                   onChange={(e) => set({ mineralisation_style: e.target.value })}
                   placeholder="Free text"
                   data-testid="input-mineralisation-style"
                   className="w-full h-11 rounded-md border border-slate-200 bg-white px-3 text-[14px]" />
        </FieldRow>

        <FieldRow icon={Package} label="Secondary / By-product commodities"
                     hint="Comma separated (e.g. 'Ag, Cu').">
          <input type="text" value={form.secondary_commodities || ""}
                   onChange={(e) => set({ secondary_commodities: e.target.value })}
                   placeholder="e.g. Ag, Cu"
                   data-testid="input-secondary-commodities"
                   className="w-full h-11 rounded-md border border-slate-200 bg-white px-3 text-[14px]" />
        </FieldRow>
      </div>

      {commodity && (
        <div className="rounded-md border border-brand-blue/20 bg-brand-blue/5 px-3 py-2 text-[11.5px] text-slate-700 flex items-start gap-2"
                data-testid="commodity-hints">
          <Sparkle size={11} className="text-brand-blue mt-0.5" weight="fill" />
          <div className="flex-1">
            <b>AI-inferred defaults for {commodity.name}:</b>{" "}
            grade unit <b>{commodity.preferred_grade_unit || "g/t"}</b>
            {density?.length === 2 && (<>, density <b>{density[0]}–{density[1]} t/m³</b></>)}.
            Everything remains editable in Step 5 (Modelling).
          </div>
        </div>
      )}
    </section>
  );
}
